import React, { useCallback, useContext, useEffect, useState } from 'react';
import ActionLink, { ACTION_LINK_HEIGHT } from '../../../../Footer/ActionLink';
import { submissionToUpdateGraphQLInput } from '../../SubmitGame/submission-helpers';
import { toast } from 'react-toastify';
import { updateSubmission as apiUpdateSubmission } from '../Mutations/update-submission-mutation';
import { SubmissionContext } from '../Context/SubmissionProvider';
import { getLatestBuild } from '../../../../../../common/build-utils';
import UploadGameFilesForm from '../GameBaseForm/UploadGameFilesForm';
import { Box } from '@mui/material';
import { Submission } from '../submission-graphql';

interface SubmissionGameBuildProps {
  goToOverview(): void;
  goToQA(submission: Submission, gameBuildId: string): void;
}

const SubmitNewVersion: React.FC<SubmissionGameBuildProps> = ({ goToQA, goToOverview }) => {
  const {
    gameBuild,
    baseForm,
    detailsForm,
    baseFormProblems,
    submission,
    setAttemptedToSave,
    modified,
    setIsModified,
    resetContextState,
    updateBaseForm,
  } = useContext(SubmissionContext);

  const [isSaving, setIsSaving] = useState(false);
  const [saveCompleted, setSaveCompleted] = useState(false);
  const [latestBuildId, setLatestBuildId] = useState<string | null>(gameBuild?.id || null);

  useEffect(() => {
    updateBaseForm({ ...baseForm, files: [] });
    // should be executed only once on start
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    if (modified) {
      setSaveCompleted(false);
    }
  }, [modified]);

  const submit = useCallback(async () => {
    if (baseFormProblems && baseFormProblems.length > 0) {
      console.error(`Form problems: ${baseFormProblems}`);
      toast.error('Please check if all the mandatory fields marked with * are filled before continuing', { autoClose: 8000 });
      setAttemptedToSave(true);
      return;
    }

    setIsSaving(true);

    try {
      const updateInput = submissionToUpdateGraphQLInput(
        submission!,
        'baseInfo',
        baseForm!,
        submission?.categoryId ? detailsForm : undefined,
      );
      const res = await apiUpdateSubmission(updateInput);
      const newSubmission = res.data?.developerSubmissionUpdate;
      if (newSubmission && newSubmission.gameBuilds && newSubmission.gameBuilds.length > 0) {
        const latestBuild = getLatestBuild(newSubmission.gameBuilds);
        setLatestBuildId(latestBuild.id);
      }

      setIsModified(false, () => {
        setSaveCompleted(!modified || saveCompleted);
      });
    } catch (e) {
      console.error(e);
      const errorMsg = e instanceof Error ? e.message : 'UNKNOWN_ERROR';
      toast.error(errorMsg, { autoClose: 5000 });
    } finally {
      setIsSaving(false);
    }
  }, [baseFormProblems, setAttemptedToSave, submission, baseForm, detailsForm, setIsModified, modified, saveCompleted]);

  if (!submission) {
    return null;
  }

  if (!latestBuildId) {
    return null;
  }

  return (
    <Box sx={{ paddingBottom: `${ACTION_LINK_HEIGHT}px` }}>
      <UploadGameFilesForm />
      <ActionLink
        primaryButton={{
          title: modified ? 'Save' : 'Go to QA',
          callback: modified ? submit : () => goToQA(submission, latestBuildId),
          isLoading: isSaving,
        }}
        primaryButtonDisabled={baseForm.uploadInProgress}
        secondaryButton={{
          title: 'Back to Overview',
          callback: () => {
            resetContextState();
            goToOverview();
          },
        }}
      />
    </Box>
  );
};

export default SubmitNewVersion;
